import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Wifi, WifiOff, TrendingUp, TrendingDown } from "lucide-react";
import { useCryptoStream, STREAMED_COINS } from "../context/CryptoStreamContext";

const formatPrice = (p) => {
  if (p == null) return "—";
  if (p >= 1000) return p.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (p >= 1) return p.toFixed(2);
  return p.toFixed(4);
};

const formatVolume = (v) => {
  if (v == null) return "—";
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
};

export default function CryptoMarketsBoard() {
  const { prices, status } = useCryptoStream();

  const rows = useMemo(
    () =>
      STREAMED_COINS.map((c) => ({
        ...c,
        live: prices[c.symbol] || null,
      })),
    [prices]
  );

  return (
    <div
      className="rounded-2xl bg-white border border-[#EAE6E1] p-6"
      data-testid="crypto-markets-board"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-1 flex items-center gap-1.5">
            Crypto markets
            <span
              className="ml-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-mono-ticker"
              style={{
                color:
                  status === "connected"
                    ? "#5C8D73"
                    : rows.some((r) => r.live)
                    ? "#A07A2C"
                    : "#C66B59",
              }}
              title={
                status === "connected"
                  ? "Binance ticker stream: connected"
                  : rows.some((r) => r.live)
                  ? "Showing latest snapshot — live stream unavailable"
                  : `Binance ticker stream: ${status}`
              }
              data-testid="crypto-markets-status"
            >
              {status === "connected" ? (
                <Wifi className="w-2.5 h-2.5" strokeWidth={2} />
              ) : (
                <WifiOff className="w-2.5 h-2.5" strokeWidth={2} />
              )}
              {status === "connected"
                ? "Live"
                : rows.some((r) => r.live)
                ? "Snapshot"
                : status}
            </span>
          </div>
          <h2 className="font-heading text-2xl tracking-tight text-[#2A2F2B]">
            Top 10 crypto · real-time
          </h2>
          <p className="text-xs text-[#6B7068] mt-0.5">
            Streaming directly from Binance — prices update by the second.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="crypto-markets-grid">
        {rows.map((r) => {
          const live = r.live;
          const up = (live?.change24h ?? 0) >= 0;
          const trendColor = up ? "#5C8D73" : "#C66B59";
          return (
            <Link
              key={r.symbol}
              to={`/asset/crypto/${r.symbol}`}
              data-testid={`crypto-markets-row-${r.symbol}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-transparent hover:border-[#EAE6E1] hover:bg-[#F9F8F6] transition-soft"
            >
              <div className="w-9 h-9 rounded-xl bg-[#FFF8F1] flex items-center justify-center font-mono-ticker text-[10px] font-semibold text-[#2A2F2B]">
                {r.symbol}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#2A2F2B] truncate">
                  {r.name}
                </div>
                <div className="text-[10px] text-[#6B7068] font-mono-ticker">
                  Vol {formatVolume(live?.volume24h)}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-ticker text-sm font-semibold text-[#2A2F2B] tabular-nums">
                  ${formatPrice(live?.price)}
                </div>
                <div
                  className="font-mono-ticker text-[11px] font-semibold flex items-center justify-end gap-0.5 tabular-nums"
                  style={{ color: live ? trendColor : "#6B7068" }}
                >
                  {live &&
                    (up ? (
                      <TrendingUp className="w-2.5 h-2.5" strokeWidth={2.5} />
                    ) : (
                      <TrendingDown className="w-2.5 h-2.5" strokeWidth={2.5} />
                    ))}
                  {live?.change24h != null
                    ? `${up ? "+" : ""}${live.change24h.toFixed(2)}%`
                    : "—"}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <p className="text-[10px] text-[#6B7068] mt-3 pt-3 border-t border-[#EAE6E1]">
        Source: Binance public WebSocket. Falls back to charts via REST if the stream drops.
      </p>
    </div>
  );
}
